import React from 'react';
import { format } from 'date-fns';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Star, CheckCircle2, Clock } from 'lucide-react';

const ProjectDetailModal = ({ open, onOpenChange, project, getStatusColor, children }) => {
    if (!project) return null;

    const formatDate = (dateString) => {
        if (!dateString) return null;
        try {
            return format(new Date(dateString), 'MMM dd, yyyy');
        } catch {
            return dateString;
        }
    };

    // Map properties to handle both list data and complete project response
    const clientName = project.clientName || project.ClientName;
    const location = project.location || project.Location;
    const sqft = project.sqft || project.Sqft;
    const status = project.status || project.CurrentStage;
    const preferredDate = project.preferredDate || project.PreferredDate;
    const scheduledDate = project.scheduledDate || project.ScheduledDate;

    // Handle both PascalCase and camelCase for API responses
    const sv1Details = project.sv1Details || project.Sv1Details;
    const sv2Details = project.sv2Details || project.Sv2Details;
    const installationDetailsData = project.installationDetails || project.InstallationDetails;

    // Supervisor name - check multiple sources
    const supervisorName = project.supervisorName ||
        sv1Details?.supervisorName ||
        sv2Details?.supervisorName ||
        project.teamInfo?.supervisorName;

    // Installation team - check multiple sources
    const installerTeam = project.installerTeam ||
        installationDetailsData?.teamName ||
        project.teamInfo?.teamName;

    // Site visit data - handle both formats
    const sv1Completed = project.siteVisit1Completed || (sv1Details?.completedAt != null);
    const sv1Date = project.siteVisit1Date || sv1Details?.scheduledDate;
    const sv2Completed = project.siteVisit2Completed || (sv2Details?.completedAt != null);
    const sv2Date = project.siteVisit2Date || sv2Details?.scheduledDate;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[85dvh] sm:max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        {clientName}
                        <span className={`text-xs px-2 py-1 rounded-full ${getStatusColor(status)}`}>
                            {status}
                        </span>
                    </DialogTitle>
                    <DialogDescription>
                        {location} • {sqft} sq.ft
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-6 mt-4">
                    {/* Project Overview */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">Preferred Date</p>
                            <p className="font-medium">{formatDate(preferredDate) || 'N/A'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">Scheduled Date</p>
                            <p className="font-medium">{formatDate(scheduledDate) || 'Not scheduled'}</p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">Assigned Supervisor</p>
                            <p className="font-medium text-teal-600">
                                {supervisorName || 'Not assigned'}
                            </p>
                        </div>
                        <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-xs text-gray-500">Installation Team</p>
                            <p className="font-medium text-blue-600">
                                {installerTeam || 'Not assigned'}
                            </p>
                        </div>
                    </div>

                    {/* Site Visit Status */}
                    <div className="border rounded-lg p-4">
                        <h4 className="font-medium text-gray-900 mb-3">Site Visit Progress</h4>
                        <div className="space-y-3">
                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {sv1Completed ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : sv1Date ? (
                                        <Clock className="w-5 h-5 text-yellow-500" />
                                    ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                    )}
                                    <div>
                                        <p className="font-medium">Site Visit 1</p>
                                        <p className="text-sm text-gray-500">Pre-work Assessment</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {sv1Completed ? (
                                        <span className="text-green-600 font-medium">Completed</span>
                                    ) : sv1Date ? (
                                        <span className="text-yellow-600">{formatDate(sv1Date)}</span>
                                    ) : (
                                        <span className="text-gray-400">Not scheduled</span>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {sv2Completed ? (
                                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                                    ) : sv2Date ? (
                                        <Clock className="w-5 h-5 text-orange-500" />
                                    ) : (
                                        <div className="w-5 h-5 rounded-full border-2 border-gray-300" />
                                    )}
                                    <div>
                                        <p className="font-medium">Site Visit 2</p>
                                        <p className="text-sm text-gray-500">Pre-installation Check</p>
                                    </div>
                                </div>
                                <div className="text-right">
                                    {sv2Completed ? (
                                        <span className="text-green-600 font-medium">Completed</span>
                                    ) : sv2Date ? (
                                        <span className="text-orange-600">{formatDate(sv2Date)}</span>
                                    ) : (
                                        <span className="text-gray-400">Not scheduled</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Installation Progress with Checklist */}
                    {installationDetailsData && (
                        <div className="border rounded-lg p-4 bg-gradient-to-r from-teal-50 to-blue-50">
                            <h4 className="font-medium text-gray-900 mb-3">Installation Progress</h4>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">Overall Completion</span>
                                    <span className="font-bold text-teal-700">{installationDetailsData.checklistProgressPercentage || 0}%</span>
                                </div>
                                <div className="w-full bg-gray-200 rounded-full h-3">
                                    <div
                                        className="bg-gradient-to-r from-teal-500 to-blue-500 h-3 rounded-full transition-all"
                                        style={{ width: `${installationDetailsData.checklistProgressPercentage || 0}%` }}
                                    />
                                </div>

                                {/* Installation Checklist Preview */}
                                {installationDetailsData.checklist && installationDetailsData.checklist.length > 0 && (
                                    <div className="mt-4 space-y-2">
                                        <h5 className="font-medium text-gray-800 text-sm">Installation Checklist</h5>
                                        <div className="bg-white rounded-lg p-3 space-y-2 max-h-60 overflow-y-auto">
                                            {installationDetailsData.checklist.map((item, index) => (
                                                <div
                                                    key={index}
                                                    className={`flex items-center gap-2 p-2 rounded ${item.isCompleted ? 'bg-green-50' : 'bg-gray-50'
                                                        }`}
                                                >
                                                    {item.isCompleted ? (
                                                        <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                                                    ) : (
                                                        <div className="w-4 h-4 rounded-full border-2 border-gray-300 flex-shrink-0" />
                                                    )}
                                                    <span className={`text-sm ${item.isCompleted ? 'text-green-900' : 'text-gray-600'}`}>
                                                        {item.itemName || item.name}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="text-xs text-gray-500 mt-2">
                                            {installationDetailsData.checklist.filter(item => item.isCompleted).length} of {installationDetailsData.checklist.length} items completed
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Rating for Completed projects */}
                    {status === 'Completed' && project.rating && (
                        <div className="border rounded-lg p-4">
                            <h4 className="font-medium text-gray-900 mb-2">Customer Rating</h4>
                            <div className="flex items-center gap-2">
                                {[...Array(5)].map((_, i) => (
                                    <Star
                                        key={i}
                                        className={`w-5 h-5 ${i < project.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                                    />
                                ))}
                                <span className="ml-2 text-gray-600">{project.rating}/5</span>
                            </div>
                        </div>
                    )}

                    {/* Rework Info if applicable */}
                    {project.reworkRequired && (
                        <div className="border rounded-lg p-4 bg-orange-50 border-orange-200">
                            <h4 className="font-medium text-orange-700 mb-2">Rework Required</h4>
                            <p className="text-gray-700">{project.reworkDetails}</p>
                            {project.revisedQuote && (
                                <p className="mt-2 font-medium text-orange-600">
                                    Revised Quote: ₹{project.revisedQuote.toLocaleString()}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Custom Action Buttons (for admin actions, etc.) */}
                    {children && (
                        <div className="pt-4 border-t space-y-2">
                            {children}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ProjectDetailModal;
